import { Route } from "lucide-react";
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useSession } from '@/lib/session'
import { useI18n } from '@/i18n'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Wrap a page to require an account.
 *
 *   <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
 *
 * IT MUST WAIT FOR `loading`. Redirecting before we know who the visitor is
 * throws signed-in people onto the login page every time they reload — a
 * classic bug, and a hard one to see, because it only happens on the first
 * render.
 *
 * THIS IS EXPERIENCE, NOT SECURITY. Real permission is decided on the server
 * (`requireUser` / `requireAdmin` in server/routes/index.ts). Hiding a button
 * without checking server-side still leaves the data one curl away.
 */
export function RequireAuth({ children, admin }: { children: ReactNode; admin?: boolean }) {
  const { user, loading, isAdmin } = useSession()
  const { t } = useI18n()
  const location = useLocation()

  if (loading) {
    return (
      <div data-upbo-src="src/components/RequireAuth.tsx:29" className="mx-auto w-full max-w-6xl space-y-4 px-6 py-24">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-80" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!user) {
    // Remember where they were heading, so signing in lands them there.
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />
  }

  if (admin && !isAdmin) {
    return (
      <div data-upbo-src="src/components/RequireAuth.tsx:44" className="mx-auto flex w-full max-w-6xl flex-col items-start gap-4 px-6 py-24">
        <h1 data-upbo-src="src/components/RequireAuth.tsx:45" className="text-3xl font-semibold tracking-tight">{t('guard.forbidden.title')}</h1>
        <p data-upbo-src="src/components/RequireAuth.tsx:46" className="max-w-md text-muted-foreground">{t('guard.forbidden.body')}</p>
      </div>
    )
  }

  return <>{children}</>
}
